import { baseApi } from '@/store/api/baseApi';
import type {
  CustomerReport,
  InventoryReport,
  OverviewReport,
  ReportQueryParams,
  SalesReport,
} from '@/types/report';

function buildReportQuery(params: ReportQueryParams): string {
  const searchParams = new URLSearchParams();
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.groupBy) searchParams.set('groupBy', params.groupBy);
  if (params.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  return searchParams.toString();
}

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOverviewReport: builder.query<OverviewReport, ReportQueryParams>({
      query: (params) => `/reports/overview?${buildReportQuery(params)}`,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildReportQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildReportQuery(currentArg ?? {}) !== buildReportQuery(previousArg ?? {}),
      providesTags: [{ type: 'Reports', id: 'OVERVIEW' }],
    }),
    getSalesReport: builder.query<SalesReport, ReportQueryParams>({
      query: (params) => `/reports/sales?${buildReportQuery(params)}`,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildReportQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildReportQuery(currentArg ?? {}) !== buildReportQuery(previousArg ?? {}),
      providesTags: [{ type: 'Reports', id: 'SALES' }],
    }),
    getInventoryReport: builder.query<InventoryReport, ReportQueryParams>({
      query: (params) => `/reports/inventory?${buildReportQuery(params)}`,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildReportQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildReportQuery(currentArg ?? {}) !== buildReportQuery(previousArg ?? {}),
      providesTags: [{ type: 'Reports', id: 'INVENTORY' }],
    }),
    getCustomerReport: builder.query<CustomerReport, ReportQueryParams>({
      query: (params) => `/reports/customers?${buildReportQuery(params)}`,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildReportQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildReportQuery(currentArg ?? {}) !== buildReportQuery(previousArg ?? {}),
      providesTags: [{ type: 'Reports', id: 'CUSTOMERS' }],
    }),
  }),
});

export const {
  useGetOverviewReportQuery,
  useGetSalesReportQuery,
  useGetInventoryReportQuery,
  useGetCustomerReportQuery,
} = reportsApi;
